import {a1ToPos, posToA1} from 'ghostban';
import {RectangleArea} from '@types';

export {a1ToPos, posToA1};

export const normalizeArea = (
    a1: string,
    b1: string
): {min: string; max: string} => {
    const a = a1ToPos(a1);
    const b = a1ToPos(b1);
    if (a.x < 0 || a.y < 0 || b.x < 0 || b.y < 0) return {min: a1, max: b1};

    const minY = Math.max(a.y, b.y); // higher y = lower row = min A1 row
    const maxY = Math.min(a.y, b.y); // lower y = higher row = max A1 row
    const minX = Math.min(a.x, b.x);
    const maxX = Math.max(a.x, b.x);

    return {
        min: posToA1(minX, minY),
        max: posToA1(maxX, maxY),
    };
};

export const isPosInArea = (
    x: number,
    y: number,
    area: {min: string; max: string}
): boolean => {
    const minPos = a1ToPos(area.min);
    const maxPos = a1ToPos(area.max);
    if (minPos.x < 0 || minPos.y < 0 || maxPos.x < 0 || maxPos.y < 0)
        return false;

    const topY = Math.min(minPos.y, maxPos.y);
    const bottomY = Math.max(minPos.y, maxPos.y);
    const leftX = Math.min(minPos.x, maxPos.x);
    const rightX = Math.max(minPos.x, maxPos.x);

    return x >= leftX && x <= rightX && y >= topY && y <= bottomY;
};

/**
 * Builds a preventMoveMat from player allowed/disallowed area metadata.
 *
 * - If playerAllowedAreas is non-empty, all positions outside those areas are
 *   blocked (whitelist mode).
 * - Positions inside playerDisallowedAreas are always blocked (blacklist mode).
 * - The two rules compose: disallowed areas take priority over allowed areas.
 *
 * Returns a boardSize×boardSize matrix where 1 = blocked, 0 = allowed.
 */
export const calcAreaPreventMoveMat = (
    boardSize: number,
    playerAllowedAreas: RectangleArea[],
    playerDisallowedAreas: RectangleArea[]
): number[][] => {
    const mat: number[][] = Array.from({length: boardSize}, () =>
        Array<number>(boardSize).fill(0)
    );

    // Whitelist: if any allowed areas are defined, block everything first,
    // then unblock only the positions inside those areas.
    if (playerAllowedAreas.length > 0) {
        for (let x = 0; x < boardSize; x++) {
            for (let y = 0; y < boardSize; y++) {
                mat[x][y] = 1;
            }
        }
        for (const area of playerAllowedAreas) {
            for (let x = 0; x < boardSize; x++) {
                for (let y = 0; y < boardSize; y++) {
                    if (isPosInArea(x, y, area)) {
                        mat[x][y] = 0;
                    }
                }
            }
        }
    }

    // Blacklist: block positions inside disallowed areas.
    for (const area of playerDisallowedAreas) {
        for (let x = 0; x < boardSize; x++) {
            for (let y = 0; y < boardSize; y++) {
                if (isPosInArea(x, y, area)) {
                    mat[x][y] = 1;
                }
            }
        }
    }

    return mat;
};
